/**
 * First-party, privacy-friendly funnel events (no PII, Do Not Track / GPC respected, no third-party scripts).
 * Batched: events queue and flush every 2 s, and on page hide with `keepalive`. First touch (`?ref`, `utm_*`) is read
 * from the landing URL and kept in memory only (nothing in cookies or web storage, so no consent banner); it is sent
 * with each batch and the server keeps the first one it sees against the visitor's cookieless daily id.
 */
type Touch = { ref?: string; utmSource?: string; utmMedium?: string; utmCampaign?: string };
type Props = Record<string, string | number | boolean>;

const dnt = (): boolean => {
  if (typeof navigator === 'undefined') return true;
  const n = navigator as Navigator & { globalPrivacyControl?: boolean };
  return n.doNotTrack === '1' || n.globalPrivacyControl === true;
};

let touch: Touch | undefined;
const readTouch = (): Touch | undefined => touch;

let queue: { name: string; props?: Props }[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;

export const flush = (keepalive = false): void => {
  if (timer) { clearTimeout(timer); timer = null; }
  if (queue.length === 0) return;
  const events = queue.splice(0, 20);
  const touch = readTouch();
  void fetch('/api/trpc/analytics.track', {
    method: 'POST', credentials: 'include', keepalive,
    headers: { 'content-type': 'application/json', 'x-keel-csrf': '1' },
    body: JSON.stringify({ json: { events, ...(touch ? { touch } : {}) } }),
  }).catch(() => undefined);
  if (queue.length) flush(keepalive);
};

/** Record a funnel event (names the API accepts: site_visit, start_view, onboarding_step_1…4, …). */
export const track = (name: string, props?: Props): void => {
  if (dnt() || import.meta.env.MODE === 'test') return;
  queue.push(props ? { name, props } : { name });
  timer ??= setTimeout(() => flush(), 2000);
};

/** Once per page load: remember where the visitor came from; a `?ref` (from the marketing site) is a site visit. */
export const captureFirstTouch = (): void => {
  if (dnt() || typeof window === 'undefined') return;
  const q = new URLSearchParams(window.location.search);
  const t: Touch = {};
  const ref = q.get('ref'), src = q.get('utm_source'), med = q.get('utm_medium'), camp = q.get('utm_campaign');
  if (ref) t.ref = ref.slice(0, 64);
  if (src) t.utmSource = src.slice(0, 64);
  if (med) t.utmMedium = med.slice(0, 64);
  if (camp) t.utmCampaign = camp.slice(0, 64);
  if (Object.keys(t).length === 0) return;
  touch ??= t;
  track('site_visit', { path: window.location.pathname.slice(0, 40) });
  window.addEventListener('pagehide', () => flush(true));
};

/** First touch for signup calls (stored server-side against the visitor). */
export const firstTouch = (): Touch | undefined => readTouch();
